import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { CheckCircle, ExternalLink, Send, Loader2 } from 'lucide-react';
import { publicApi } from '../../lib/api';

/*
 * Abschluss (Feedback 04.09., 63:08 / 66:45):
 * - optionales Feedback zum Bewertungsprozess (Textbox, keine Sterne)  #29
 * - Link zur Website der Hausverwaltung statt zur Immoverita-Startseite  #31
 */
export default function DoneStep({ email, reviewId, tenant, headline = 'Bewertung abgeschlossen' }) {
  const [feedback, setFeedback] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const website = tenant?.website ? (tenant.website.startsWith('http') ? tenant.website : `https://${tenant.website}`) : null;

  const sendFeedback = async () => {
    if (!feedback.trim() || !reviewId) return;
    setSending(true);
    try {
      await publicApi.sendProcessFeedback(reviewId, feedback.trim());
      setSent(true);
      toast.success('Danke für Ihr Feedback!');
    } catch {
      toast.error('Konnte nicht gesendet werden');
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4">
      <Card className="card-shadow">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-[hsl(var(--success))]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-1">{headline}</h2>
            <p className="text-muted-foreground">Vielen Dank für Ihre Zeit und Ihr ehrliches Feedback.</p>
            {email && (
              <p className="text-sm text-muted-foreground mt-2">
                Ihre E-Mail <strong>{email}</strong> wurde hinterlegt — damit können Sie Ihre Bewertung später bearbeiten.
              </p>
            )}
          </div>

          {website && (
            <a href={website} target="_blank" rel="noopener noreferrer" className="inline-block">
              <Button variant="secondary">
                Zur Website von {tenant.display_name}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          )}
        </CardContent>
      </Card>

      {reviewId && !sent && (
        <Card>
          <CardContent className="pt-5 pb-5 space-y-3">
            <div>
              <p className="text-sm font-medium">Wie fanden Sie den Bewertungsprozess?</p>
              <p className="text-xs text-muted-foreground">Optional — hilft uns, das Verfahren zu verbessern.</p>
            </div>
            <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3}
                      placeholder="Was war gut, was hat gestört?" data-testid="process-feedback-textarea" />
            <Button variant="outline" size="sm" onClick={sendFeedback} disabled={sending || !feedback.trim()}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Feedback senden
            </Button>
          </CardContent>
        </Card>
      )}
      {sent && <p className="text-center text-sm text-muted-foreground">Danke — Ihr Feedback ist angekommen.</p>}
    </motion.div>
  );
}
